import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { Classes, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import apis from 'browser/app/models/apis'
import { MobileModal } from 'browser/mobile/elements/modal/modal'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { JSONSchemaResolver } from 'shared-libs/resolvers/json-schema-resolver'
import {
  addFilter,
  buildDisplayFilters,
  getExclusiveSetKey,
  getFilterFeedbackText,
  getFilterIdentity,
  getNaturalSpeechFilterText,
  getNormalizedFiltersFromProps,
  IDisplayFilter,
  ISearchFilterConfig,
  normalizeAvailableFilter,
  removeFilterByIdentity,
} from 'shared-libs/helpers/search-filters'

import './_search-filters.scss'

export type { ISearchFilterConfig, IDisplayFilter }

interface IMobileSearchFiltersProps {
  filters?: ISearchFilterConfig[]
  availableFilters?: ISearchFilterConfig[]
  entitySchema?: any
  onFiltersChange?: (
    filters: ISearchFilterConfig[],
    availableFilters?: ISearchFilterConfig[]
  ) => void
  className?: string
}

interface IMobileSearchFiltersState {
  draftAppliedFilters: ISearchFilterConfig[]
  draftAvailableFilters: ISearchFilterConfig[]
  exclusiveSetFeedbackText?: string
  highlightedAppliedFilterIdentity?: string
  pillContainerWidth: number
  pillRowsByKey: Record<string, number>
}

const EXCLUSIVE_SET_FEEDBACK_DURATION_MS = 2500

/**
 * MW port of the NM `SearchFilters` chip UI. Same layer separation as the
 * DW version: header pills with overflow, tap-to-open sheet, draft state,
 * exclusive-set replacement, natural-language descriptions. The sheet uses
 * `MobileModal` (full-height) rather than the DW centered dialog.
 */
export class MobileSearchFilters extends React.Component<
  IMobileSearchFiltersProps,
  IMobileSearchFiltersState
> {
  private schemaResolver: JSONSchemaResolver
  private exclusiveSetFeedbackTimeout?: ReturnType<typeof setTimeout>
  private pillContainerRef = React.createRef<HTMLDivElement>()
  private pillContainerObserver?: ResizeObserver
  private pillElementsByKey: Record<string, HTMLDivElement | null> = {}
  private highlightClearTimeout?: ReturnType<typeof setTimeout>
  private openSheetNode?: Element

  constructor(props: IMobileSearchFiltersProps) {
    super(props)
    this.schemaResolver = new JSONSchemaResolver(apis)
    this.state = {
      draftAppliedFilters: [],
      draftAvailableFilters: [],
      exclusiveSetFeedbackText: undefined,
      highlightedAppliedFilterIdentity: undefined,
      pillContainerWidth: 0,
      pillRowsByKey: {},
    }
  }

  public componentDidMount() {
    if (this.pillContainerRef.current && typeof ResizeObserver !== 'undefined') {
      this.pillContainerObserver = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        const nextWidth = Math.round(entry.contentRect.width)
        this.setState((prev) =>
          prev.pillContainerWidth === nextWidth
            ? null
            : { pillContainerWidth: nextWidth, pillRowsByKey: {} }
        )
      })
      this.pillContainerObserver.observe(this.pillContainerRef.current)
    }
  }

  public componentWillUnmount() {
    this.pillContainerObserver?.disconnect()
    this.clearExclusiveSetFeedbackTimeout()
    if (this.highlightClearTimeout) clearTimeout(this.highlightClearTimeout)
  }

  public render() {
    const { className } = this.props
    const {
      appliedFilters: normalizedAppliedFilters,
      availableFilters: normalizedAvailableFilters,
    } = getNormalizedFiltersFromProps(this.props.filters, this.props.availableFilters)

    const displayFilters = this.getDisplayFilters(normalizedAppliedFilters)
    const availableDisplayFilters = this.getDisplayFilters(normalizedAvailableFilters)

    const hasVisibleApplied = !_.isEmpty(displayFilters)
    const hasVisibleAvailable = !_.isEmpty(availableDisplayFilters)
    const isSheetOpenable = hasVisibleAvailable || hasVisibleApplied

    if (!hasVisibleApplied && !hasVisibleAvailable) {
      return null
    }

    const { visibleDisplayFilters, hiddenFilterCount } = this.getVisibleAppliedPills(displayFilters)
    let pillsToRender = visibleDisplayFilters
    let morePillsCount = hiddenFilterCount

    if (morePillsCount > 0 && pillsToRender.length > 0) {
      pillsToRender = pillsToRender.slice(0, -1)
      morePillsCount += 1
    }

    return (
      <div
        className={classNames('mw-searchFilters', className, {
          'mw-searchFilters--openable': isSheetOpenable,
        })}
        role={isSheetOpenable ? 'button' : undefined}
        tabIndex={isSheetOpenable ? 0 : -1}
        onClick={isSheetOpenable ? this.handleOpenSheet : undefined}
        onKeyDown={(e) => {
          if (!isSheetOpenable) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.handleOpenSheet()
          }
        }}
      >
        {/*
          Entry-point hint — italic plain text at the TOP of the container
          so the whole surface reads as one tappable region rather than a
          collection of pills with a separate "Add Filters" chip at the
          end. The outer container owns the tap target (see `onClick`
          above); this text is presentational only.
        */}
        {isSheetOpenable && (
          <div className="mw-searchFilters-hint">
            <Icon icon={IconNames.FILTER_LIST} className="mw-searchFilters-hint-icon" />
            <span className="mw-searchFilters-hint-text">
              {!hasVisibleApplied && hasVisibleAvailable
                ? 'No filters applied—tap to view / add / remove filters'
                : 'Tap to view / add / remove filters'}
            </span>
          </div>
        )}
        <div className="mw-searchFilters-pills" ref={this.pillContainerRef}>
          {_.map(pillsToRender, ({ key, label, value, isClearable }) => (
            <div
              key={key}
              className={classNames('mw-searchFilters-pill', {
                'mw-searchFilters-pill--fixed': !isClearable,
              })}
              ref={(el) => this.registerPillRef(key, el)}
            >
              <span className="mw-searchFilters-pillLabel">{label}:</span>
              <span className="mw-searchFilters-pillValue">{value}</span>
            </div>
          ))}
          {morePillsCount > 0 && (
            <div className="mw-searchFilters-pill mw-searchFilters-pill--more">
              {`+ ${morePillsCount} more`}
            </div>
          )}
        </div>
      </div>
    )
  }

  private registerPillRef = (key: string, el: HTMLDivElement | null) => {
    this.pillElementsByKey[key] = el
    if (!el) return
    requestAnimationFrame(() => {
      const containerTop = this.pillContainerRef.current?.getBoundingClientRect().top ?? 0
      const rect = el.getBoundingClientRect()
      const row = Math.round(rect.top - containerTop)
      this.setState((prev) => {
        if (prev.pillRowsByKey[key] === row) return null
        return { pillRowsByKey: { ...prev.pillRowsByKey, [key]: row } }
      })
    })
  }

  private getVisibleAppliedPills = (displayFilters: IDisplayFilter[]) => {
    const { pillRowsByKey } = this.state
    const hasMeasured = _.every(displayFilters, (f) => !_.isNil(pillRowsByKey[f.key]))
    if (!hasMeasured) {
      return { visibleDisplayFilters: displayFilters, hiddenFilterCount: 0 }
    }
    const uniqueRows = _.sortBy(_.uniq(_.map(displayFilters, (f) => pillRowsByKey[f.key])))
    if (uniqueRows.length <= 2) {
      return { visibleDisplayFilters: displayFilters, hiddenFilterCount: 0 }
    }
    const secondRow = uniqueRows[1]
    const visibleDisplayFilters = _.filter(
      displayFilters,
      (f) => pillRowsByKey[f.key] <= secondRow
    )
    return {
      visibleDisplayFilters,
      hiddenFilterCount: Math.max(0, displayFilters.length - visibleDisplayFilters.length),
    }
  }

  // ─── Sheet lifecycle ─────────────────────────────────────────────────────

  private handleOpenSheet = () => {
    const {
      appliedFilters: normalizedAppliedFilters,
      availableFilters: normalizedAvailableFilters,
    } = getNormalizedFiltersFromProps(this.props.filters, this.props.availableFilters)

    this.setState({
      draftAppliedFilters: _.cloneDeep(normalizedAppliedFilters),
      draftAvailableFilters: _.cloneDeep(normalizedAvailableFilters),
    })

    this.openSheetNode = MobileModal.open(
      <MobileSearchFiltersSheet
        ownerRef={this}
        onCancel={this.handleCancelSheet}
        onApply={this.handleApplySheet}
      />,
      { modalDialogClassName: 'mw-searchFilters-sheetDialog' }
    ) as Element
  }

  private closeSheetOverlay = () => {
    if (this.openSheetNode) {
      OverlayManager.closeOverlay(this.openSheetNode as HTMLElement)
      this.openSheetNode = undefined
    }
  }

  private handleCancelSheet = () => {
    this.clearExclusiveSetFeedbackTimeout()
    this.setState({
      draftAppliedFilters: [],
      draftAvailableFilters: [],
      exclusiveSetFeedbackText: undefined,
    })
    this.closeSheetOverlay()
  }

  private handleApplySheet = () => {
    const { onFiltersChange } = this.props
    onFiltersChange?.(this.state.draftAppliedFilters, this.state.draftAvailableFilters)
    this.clearExclusiveSetFeedbackTimeout()
    this.setState({
      draftAppliedFilters: [],
      draftAvailableFilters: [],
      exclusiveSetFeedbackText: undefined,
    })
    this.closeSheetOverlay()
  }

  // ─── Sheet actions (owner-exposed) ───────────────────────────────────────

  public handleMoveFilterToAvailable = (filterItem: IDisplayFilter) => {
    if (!filterItem.isClearable) return

    this.setState((prev) => ({
      draftAppliedFilters: removeFilterByIdentity(prev.draftAppliedFilters, filterItem.identity),
      draftAvailableFilters: addFilter(
        prev.draftAvailableFilters,
        normalizeAvailableFilter(filterItem.filter)
      ),
    }))
  }

  public handleMoveFilterToApplied = (filterItem: IDisplayFilter) => {
    let replacementFeedback: { appliedText: string; replacedText: string } | undefined
    let addedIdentity: string | undefined

    this.setState(
      (prev) => {
        const filterToApply = normalizeAvailableFilter(filterItem.filter)
        const filterToApplyIdentity = getFilterIdentity(filterToApply)
        const filterExclusiveSetKey = getExclusiveSetKey(filterToApply)
        addedIdentity = filterToApplyIdentity

        let nextAvailable = removeFilterByIdentity(prev.draftAvailableFilters, filterItem.identity)
        let nextApplied = prev.draftAppliedFilters

        if (!_.isNil(filterExclusiveSetKey)) {
          const conflicting = _.filter(nextApplied, (a) => {
            return (
              getExclusiveSetKey(a) === filterExclusiveSetKey &&
              getFilterIdentity(a) !== filterToApplyIdentity
            )
          })
          const firstConflict = _.first(conflicting)
          if (firstConflict) {
            replacementFeedback = {
              appliedText: getFilterFeedbackText(
                filterToApply,
                this.props.entitySchema,
                this.schemaResolver
              ),
              replacedText: getFilterFeedbackText(
                firstConflict,
                this.props.entitySchema,
                this.schemaResolver
              ),
            }
          }
          nextApplied = _.filter(nextApplied, (a) => {
            return (
              getExclusiveSetKey(a) !== filterExclusiveSetKey ||
              getFilterIdentity(a) === filterToApplyIdentity
            )
          })
          nextAvailable = _.reduce(
            conflicting,
            (acc, cf) => addFilter(acc, normalizeAvailableFilter(cf)),
            nextAvailable
          )
        }

        nextApplied = addFilter(nextApplied, filterToApply)
        return {
          draftAppliedFilters: nextApplied,
          draftAvailableFilters: nextAvailable,
        }
      },
      () => {
        if (addedIdentity) {
          this.highlightAppliedFilterRow(addedIdentity)
        }
        if (replacementFeedback) {
          this.showExclusiveSetFeedback(
            `Replaced "${replacementFeedback.replacedText}" with "${replacementFeedback.appliedText}"`
          )
        }
      }
    )
  }

  public getDisplayFilters = (filters: ISearchFilterConfig[]): IDisplayFilter[] => {
    return buildDisplayFilters(filters, this.props.entitySchema, this.schemaResolver)
  }

  public getFilterDescriptionText = (item: IDisplayFilter): string => {
    return (
      item.helpText ||
      getNaturalSpeechFilterText(
        item.filter,
        item.label,
        item.value,
        this.props.entitySchema?.translations
      )
    )
  }

  private highlightAppliedFilterRow = (identity: string) => {
    this.setState({ highlightedAppliedFilterIdentity: identity })
    if (this.highlightClearTimeout) clearTimeout(this.highlightClearTimeout)
    this.highlightClearTimeout = setTimeout(() => {
      this.setState({ highlightedAppliedFilterIdentity: undefined })
      this.highlightClearTimeout = undefined
    }, 900)
  }

  private showExclusiveSetFeedback = (message: string) => {
    this.clearExclusiveSetFeedbackTimeout()
    this.setState({ exclusiveSetFeedbackText: message })
    this.exclusiveSetFeedbackTimeout = setTimeout(() => {
      this.setState({ exclusiveSetFeedbackText: undefined })
      this.exclusiveSetFeedbackTimeout = undefined
    }, EXCLUSIVE_SET_FEEDBACK_DURATION_MS)
  }

  private clearExclusiveSetFeedbackTimeout = () => {
    if (this.exclusiveSetFeedbackTimeout) {
      clearTimeout(this.exclusiveSetFeedbackTimeout)
      this.exclusiveSetFeedbackTimeout = undefined
    }
  }

  public get draftAppliedFilters(): ISearchFilterConfig[] {
    return this.state.draftAppliedFilters
  }
  public get draftAvailableFilters(): ISearchFilterConfig[] {
    return this.state.draftAvailableFilters
  }
  public get exclusiveSetFeedbackText(): string | undefined {
    return this.state.exclusiveSetFeedbackText
  }
  public get highlightedAppliedFilterIdentity(): string | undefined {
    return this.state.highlightedAppliedFilterIdentity
  }
}

interface ISheetProps {
  ownerRef: MobileSearchFilters
  onCancel: () => void
  onApply: () => void
}

class MobileSearchFiltersSheet extends React.Component<ISheetProps> {
  private unsubscribe?: () => void

  public componentDidMount() {
    // Subscribe to owner state changes so the sheet re-renders as the user
    // adds/removes filters.
    const originalSetState = this.props.ownerRef.setState.bind(this.props.ownerRef)
    this.props.ownerRef.setState = ((...args: any[]) => {
      const cb = args[1]
      const wrappedCb = () => {
        this.forceUpdate()
        cb?.()
      }
      return originalSetState(args[0], wrappedCb)
    }) as any
    this.unsubscribe = () => {
      this.props.ownerRef.setState = originalSetState
    }
  }

  public componentWillUnmount() {
    this.unsubscribe?.()
  }

  public render() {
    const owner = this.props.ownerRef
    const draftApplied = owner.getDisplayFilters(owner.draftAppliedFilters)
    const draftAvailable = owner.getDisplayFilters(owner.draftAvailableFilters)
    const exclusiveSetFeedbackText = owner.exclusiveSetFeedbackText

    return (
      <div className="mw-searchFilters-sheet">
        <div className="mw-searchFilters-sheet-header">
          <h5 className="mw-searchFilters-sheet-title">Filters</h5>
          <button
            type="button"
            className={classNames(Classes.MINIMAL, 'mw-searchFilters-sheet-closeButton')}
            onClick={this.props.onCancel}
            aria-label="Close"
          >
            <Icon icon={IconNames.CROSS} />
          </button>
        </div>

        <div className="mw-searchFilters-sheet-body">
          <div className="mw-searchFilters-sheet-section">
            <div className="mw-searchFilters-sheet-sectionTitle">
              Applied Filters ({draftApplied.length})
            </div>
            <div className="mw-searchFilters-sheet-list">
              {_.isEmpty(draftApplied) ? (
                <div className="mw-searchFilters-sheet-emptyState">No applied filters</div>
              ) : (
                _.map(draftApplied, (item) => this.renderAppliedRow(item))
              )}
            </div>
          </div>

          {!_.isNil(exclusiveSetFeedbackText) && (
            <div className="mw-searchFilters-sheet-feedback">{exclusiveSetFeedbackText}</div>
          )}

          <div className="mw-searchFilters-sheet-section">
            <div className="mw-searchFilters-sheet-sectionTitle">Available Filters</div>
            <div className="mw-searchFilters-sheet-list">
              {_.isEmpty(draftAvailable) ? (
                <div className="mw-searchFilters-sheet-emptyState">No available filters</div>
              ) : (
                _.map(draftAvailable, (item) => this.renderAvailableRow(item))
              )}
            </div>
          </div>
        </div>

        <div className="mw-searchFilters-sheet-footer">
          <button
            type="button"
            className={classNames(Classes.BUTTON, 'mw-searchFilters-sheet-cancelButton')}
            onClick={this.props.onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={classNames(
              Classes.BUTTON,
              Classes.INTENT_PRIMARY,
              'mw-searchFilters-sheet-applyButton'
            )}
            onClick={this.props.onApply}
          >
            Apply
          </button>
        </div>
      </div>
    )
  }

  private renderAppliedRow = (item: IDisplayFilter) => {
    const owner = this.props.ownerRef
    const descriptionText = owner.getFilterDescriptionText(item)
    const isHighlighted = owner.highlightedAppliedFilterIdentity === item.identity

    return (
      <button
        key={item.key}
        type="button"
        className={classNames('mw-searchFilters-sheet-row', {
          'mw-searchFilters-sheet-row--highlighted': isHighlighted,
          'mw-searchFilters-sheet-row--nonClearable': !item.isClearable,
        })}
        disabled={!item.isClearable}
        onClick={() => owner.handleMoveFilterToAvailable(item)}
      >
        <div className="mw-searchFilters-sheet-rowMain">
          <div
            className={classNames('mw-searchFilters-pill', 'mw-searchFilters-pill--sheet', {
              'mw-searchFilters-pill--fixed': !item.isClearable,
            })}
          >
            <span className="mw-searchFilters-pillLabel">{item.label}:</span>
            <span className="mw-searchFilters-pillValue">{item.value}</span>
          </div>
          <div className="mw-searchFilters-sheet-rowHelp">{descriptionText}</div>
        </div>
        {item.isClearable ? (
          <div className="mw-searchFilters-sheet-rowIcon">
            <Icon icon={IconNames.CROSS} />
          </div>
        ) : (
          <div className="mw-searchFilters-sheet-rowNonClearableHint">Cannot remove</div>
        )}
      </button>
    )
  }

  private renderAvailableRow = (item: IDisplayFilter) => {
    const owner = this.props.ownerRef
    const descriptionText = owner.getFilterDescriptionText(item)

    return (
      <button
        key={item.key}
        type="button"
        className="mw-searchFilters-sheet-row"
        onClick={() => owner.handleMoveFilterToApplied(item)}
      >
        <div className="mw-searchFilters-sheet-rowMain">
          <div className={classNames('mw-searchFilters-pill', 'mw-searchFilters-pill--sheet')}>
            <span className="mw-searchFilters-pillLabel">{item.label}:</span>
            <span className="mw-searchFilters-pillValue">{item.value}</span>
          </div>
          <div className="mw-searchFilters-sheet-rowHelp">{descriptionText}</div>
        </div>
        <div className="mw-searchFilters-sheet-rowIcon mw-searchFilters-sheet-rowIcon--add">
          <Icon icon={IconNames.PLUS} />
        </div>
      </button>
    )
  }
}
